/**
 * Dashboard · Dialogo "Nuevos Estudios".
 * Lista los nuevos estudios (Config -> Estudios -> Nuevos Estudios) y permite
 * desplegar cada uno para ver los voluntarios interesados (nombre, email, telefono, fecha).
 * Visible para admin y supervisor de reclutamiento.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { Flask, Users, Phone, Envelope, CaretDown, CaretRight, CheckCircle, Warning, ArrowClockwise } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EP = `${API_URL}/api/config/upcoming-studies`;

export default function UpcomingStudiesInterestsDialog({ open, onOpenChange, lang }) {
  const es = lang === 'es';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [interests, setInterests] = useState({});      // { [studyId]: rows[] }
  const [loadingId, setLoadingId] = useState(null);
  const [resendingId, setResendingId] = useState(null);

  const L = {
    title: es ? 'Nuevos Estudios' : 'Upcoming Studies',
    intro: es ? 'Estudios creados y voluntarios interesados.' : 'Created studies and interested volunteers.',
    empty: es ? 'Aun no hay nuevos estudios.' : 'No upcoming studies yet.',
    interested: es ? 'interesados' : 'interested',
    noInterest: es ? 'Nadie se ha apuntado todavia.' : 'No one has signed up yet.',
    draft: es ? 'Borrador' : 'Draft',
    published: es ? 'Publicado' : 'Published',
    crmSynced: es ? 'En CRM' : 'In CRM',
    crmPending: es ? 'CRM pendiente' : 'CRM pending',
    crmError: es ? 'CRM error' : 'CRM error',
    crmResend: es ? 'Reenviar al CRM' : 'Resend to CRM',
    crmResent: es ? 'Lead enviado al CRM' : 'Lead sent to CRM',
  };

  useEffect(() => {
    if (!open) return;
    setExpanded(null);
    setInterests({});
    setLoading(true);
    axios.get(EP, { withCredentials: true })
      .then(r => setItems(r.data || []))
      .catch(e => toast.error(errText(e)))
      .finally(() => setLoading(false));
  }, [open]);

  const toggle = async (it) => {
    if (expanded === it.id) { setExpanded(null); return; }
    setExpanded(it.id);
    if (interests[it.id]) return;            // ya cargado
    setLoadingId(it.id);
    try {
      const r = await axios.get(`${EP}/${it.id}/interests`, { withCredentials: true });
      setInterests(prev => ({ ...prev, [it.id]: r.data || [] }));
    } catch (e) {
      toast.error(errText(e));
      setInterests(prev => ({ ...prev, [it.id]: [] }));
    } finally {
      setLoadingId(null);
    }
  };

  const resendCrm = async (studyId, row) => {
    setResendingId(row.id);
    try {
      const r = await axios.post(`${EP}/interests/${row.id}/resend-crm`, {}, { withCredentials: true });
      setInterests(prev => ({
        ...prev,
        [studyId]: (prev[studyId] || []).map(x => x.id === row.id ? { ...x, ...r.data } : x),
      }));
      if (r.data?.ok) toast.success(L.crmResent);
      else toast.error(r.data?.crm_error || 'CRM error');
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setResendingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dashboard-upcoming-dialog" className="max-w-2xl overflow-hidden">
        <DialogHeader className="min-w-0">
          <DialogTitle className="flex items-center gap-2 min-w-0">
            <Flask size={18} weight="duotone" className="text-[#1f3e80] flex-shrink-0" />
            <span className="truncate">{L.title}</span>
          </DialogTitle>
          <p className="text-xs text-stone-500">{L.intro}</p>
        </DialogHeader>

        <div className="min-w-0 max-h-[65vh] overflow-y-auto pr-1">
          {loading ? (
            <p className="text-sm text-stone-400 py-8 text-center">...</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-stone-400 py-8 text-center" data-testid="dashboard-upcoming-empty">{L.empty}</p>
          ) : (
            <div className="space-y-2">
              {items.map((it) => {
                const isOpen = expanded === it.id;
                const rows = interests[it.id] || [];
                return (
                  <div key={it.id} data-testid={`dashboard-upcoming-item-${it.id}`}
                    className={`rounded-sm border ${isOpen ? 'border-[#1f3e80]/30' : 'border-stone-200'}`}>
                    <button type="button" onClick={() => toggle(it)}
                      data-testid={`dashboard-upcoming-toggle-${it.id}`}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-stone-50 min-w-0">
                      {isOpen ? <CaretDown size={15} className="text-[#1f3e80] flex-shrink-0" />
                              : <CaretRight size={15} className="text-stone-400 flex-shrink-0" />}
                      <span className="font-medium text-sm text-stone-900 truncate flex-1 min-w-0">{it.name}</span>
                      <Badge variant="outline" className={it.published
                        ? 'border-emerald-300 text-emerald-700 flex-shrink-0'
                        : 'border-stone-300 text-stone-500 flex-shrink-0'}>
                        {it.published ? L.published : L.draft}
                      </Badge>
                      <span className="flex items-center gap-1 text-xs text-[#1f3e80] font-semibold flex-shrink-0">
                        <Users size={14} weight="duotone" />{it.interest_count || 0}
                      </span>
                    </button>

                    {isOpen && (
                      <div className="border-t border-stone-100 px-3 py-2">
                        {loadingId === it.id ? (
                          <p className="text-xs text-stone-400 py-3 text-center">...</p>
                        ) : rows.length === 0 ? (
                          <p className="text-xs text-stone-400 py-3 text-center"
                            data-testid={`dashboard-upcoming-noint-${it.id}`}>{L.noInterest}</p>
                        ) : (
                          <ul className="divide-y divide-stone-100">
                            {rows.map((r) => (
                              <li key={r.id} className="py-2 min-w-0"
                                data-testid={`dashboard-upcoming-vol-${r.volunteer_id}`}>
                                <div className="flex items-center justify-between gap-2 min-w-0">
                                  <span className="text-sm font-medium text-stone-800 truncate">
                                    {r.volunteer_name || r.volunteer_id}
                                  </span>
                                  <span className="text-[11px] text-stone-400 flex-shrink-0">
                                    {(r.created_at || '').slice(0, 10)}
                                  </span>
                                </div>
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 mt-0.5 text-xs text-stone-600">
                                  {r.volunteer_email && (
                                    <a href={`mailto:${r.volunteer_email}`}
                                      className="flex items-center gap-1 hover:text-[#1f3e80] truncate">
                                      <Envelope size={13} /> {r.volunteer_email}
                                    </a>
                                  )}
                                  {r.volunteer_phone && (
                                    <a href={`tel:${r.volunteer_phone}`}
                                      className="flex items-center gap-1 hover:text-[#1f3e80]">
                                      <Phone size={13} /> {r.volunteer_phone}
                                    </a>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 mt-1" data-testid={`dashboard-upcoming-crm-${r.volunteer_id}`}>
                                  {r.crm_status === 'synced' ? (
                                    <Badge variant="outline" className="border-emerald-300 text-emerald-700 gap-1 text-[11px]">
                                      <CheckCircle size={12} weight="fill" /> {L.crmSynced}
                                    </Badge>
                                  ) : r.crm_status === 'error' ? (
                                    <Badge variant="outline" className="border-red-300 text-red-700 gap-1 text-[11px]" title={r.crm_error || ''}>
                                      <Warning size={12} weight="fill" /> {L.crmError}
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="border-amber-300 text-amber-700 gap-1 text-[11px]">
                                      {L.crmPending}
                                    </Badge>
                                  )}
                                  {r.crm_status !== 'synced' && (
                                    <Button type="button" size="sm" variant="ghost"
                                      disabled={resendingId === r.id}
                                      onClick={() => resendCrm(it.id, r)}
                                      data-testid={`dashboard-upcoming-resend-${r.volunteer_id}`}
                                      className="h-6 px-2 text-[11px] text-[#1f3e80] hover:bg-[#1f3e80]/10 gap-1">
                                      <ArrowClockwise size={12} className={resendingId === r.id ? 'animate-spin' : ''} />
                                      {L.crmResend}
                                    </Button>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
